import { forwardRef, useId, useLayoutEffect, useMemo, useState, type ChangeEvent } from "react";

import { Field } from "../Field";
import { cn } from "../index";
import { TextField } from "../TextField";

const generateCaptcha = (length: number) => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let captcha = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    captcha += characters.charAt(randomIndex);
  }

  return captcha;
};

export const CAPTCHA_FIELD_NAME = "CaptchaField";

export interface CaptchaFieldProps {
  label: string;
  placeholder?: string;
  captchaLength?: number;
  captcha?: string;
  onValidChange?: (valid: boolean) => void;
  className?: string;
}

const CaptchaField = forwardRef<HTMLDivElement, CaptchaFieldProps>((props, forwardedRef) => {
  const { label, placeholder, captchaLength = 4, captcha: captchaProp, onValidChange, className } = props;

  const generatedCaptcha = useMemo(() => captchaProp ?? generateCaptcha(captchaLength), [captchaProp, captchaLength]);

  const [value, setValue] = useState("");
  const id = useId();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onValidChange?.(newValue === generatedCaptcha);
  };

  useLayoutEffect(() => {
    onValidChange?.(value === generatedCaptcha);
  }, [onValidChange, generatedCaptcha, value]);

  return (
    <div ref={forwardedRef} className={cn("gap-10 flex w-fit items-end", className)} data-testid="captcha-field">
      <span
        className={cn("px-5 py-3.5 font-medium rounded-[10px] bg-primary-gray select-all", className)}
        data-testid="captcha"
      >
        {generatedCaptcha}
      </span>
      <Field.Root>
        <Field.Label htmlFor={id}>{label}</Field.Label>
        <TextField.Root
          id={id}
          className={cn("min-w-[200px]", className)}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
        />
      </Field.Root>
    </div>
  );
});

CaptchaField.displayName = CAPTCHA_FIELD_NAME;

export { CaptchaField };
